import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Dialog } from 'primereact/dialog';
import { Button } from '@/components/ui/button';

interface ConfirmDialogProps {
  open: boolean;
  title?: string;
  message?: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** `danger` is a deprecated alias for `destructive` (T-202 Slice 2). */
  variant?: 'destructive' | 'warning' | 'danger';
  loading?: boolean;
  onConfirm: () => void;
  onCancel?: () => void;
  onClose?: () => void;
  /** Optional extra content (e.g. a reason field) shown under the message. */
  extra?: ReactNode;
}

export function ConfirmDialog({
  open,
  title,
  message,
  description,
  confirmLabel,
  cancelLabel,
  variant = 'destructive',
  loading = false,
  onConfirm,
  onCancel,
  onClose,
  extra,
}: ConfirmDialogProps) {
  const { t } = useTranslation('common');
  const resolvedTitle = title ?? t('confirmAction');
  const resolvedConfirmLabel = confirmLabel ?? t('delete');
  const resolvedCancelLabel = cancelLabel ?? t('cancel');
  const resolvedMessage = message ?? description ?? '';
  const handleClose = onCancel ?? onClose ?? (() => {});
  // `danger` kept as a back-compat alias so existing callers don't change.
  const isWarning = variant === 'warning';
  const confirmVariant = isWarning ? 'warning' : 'destructive';

  // Right-aligned footer, canonical variants (no ad-hoc shadow classes) — matches FormDialog.
  const footer = (
    <div className="flex items-center justify-end gap-2 pt-1">
      <Button variant="outline" onClick={handleClose} disabled={loading} type="button">
        {resolvedCancelLabel}
      </Button>
      <Button variant={confirmVariant} onClick={onConfirm} disabled={loading} aria-busy={loading}>
        {loading ? (
          <span className="inline-flex items-center gap-1.5">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            {t('processing')}
          </span>
        ) : (
          resolvedConfirmLabel
        )}
      </Button>
    </div>
  );

  return (
    <Dialog
      visible={open}
      onHide={handleClose}
      header={resolvedTitle}
      footer={footer}
      style={{ width: '24rem' }}
      breakpoints={{ '640px': '95vw' }}
      closable={!loading}
      draggable={false}
      resizable={false}
    >
      <div className="flex flex-col items-center text-center py-2">
        <div className={`mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full ${
          isWarning ? 'bg-warning/15' : 'bg-destructive/10'
        }`}>
          <AlertTriangle className={`h-7 w-7 ${isWarning ? 'text-warning-text' : 'text-destructive-text'}`} />
        </div>
        <p className="text-sm text-muted-foreground leading-relaxed">{resolvedMessage}</p>
        {extra ? <div className="w-full">{extra}</div> : null}
      </div>
    </Dialog>
  );
}

